import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as Fi from 'react-icons/fi';
import TimerCompleteModal from './TimerCompleteModal.jsx';

const PRESETS = [
  { id: 'interval', label: '局間休息', emoji: '☕', seconds: 120 },
  { id: 'court', label: '擦地暫停', emoji: '🧹', seconds: 60 },
  { id: 'shuttle', label: '換球', emoji: '🏸', seconds: 30 },
  { id: 'medical', label: '醫療暫停', emoji: '🩺', seconds: 180 },
];

const TimerSheet = ({ open, onClose }) => {
  const [active, setActive] = useState(null);
  const [custom, setCustom] = useState('');
  const [showComplete, setShowComplete] = useState(false);
  const intervalRef = useRef(null);

  const start = (label, emoji, seconds) => {
    clearInterval(intervalRef.current);
    setActive({ label, emoji, remaining: seconds, total: seconds });
    intervalRef.current = setInterval(() => {
      setActive(prev => {
        if (!prev) return null;
        if (prev.remaining <= 1) {
          clearInterval(intervalRef.current);
          // Play sound and vibrate
          try {
            navigator.vibrate?.([200, 100, 200, 100, 200]);
          } catch {}
          try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.value = 880;
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start();
            gain.gain.exponentialRampToValueAtTime(0.00001, ctx.currentTime + 0.3);
            osc.stop(ctx.currentTime + 0.3);
          } catch {}
          // Show completion modal
          setShowComplete(true);
          return { ...prev, remaining: 0 };
        }
        return { ...prev, remaining: prev.remaining - 1 };
      });
    }, 1000);
  };

  const stop = () => {
    clearInterval(intervalRef.current);
    setActive(null);
  };

  const handleCompleteClose = () => {
    setShowComplete(false);
    stop();
  };

  useEffect(() => () => clearInterval(intervalRef.current), []);

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  const progress = active ? active.remaining / active.total : 1;

  return (
    <>
      <AnimatePresence>
        {open && (
          <div className="fixed inset-0 z-[160] flex items-end md:items-center md:justify-center pointer-events-none">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 pointer-events-auto"
              style={{
                background: 'rgba(2, 6, 23, 0.85)',
                backdropFilter: 'blur(8px)',
              }}
              onClick={onClose}
            />
            <motion.div
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 28, stiffness: 320 }}
              className="relative z-10 w-full max-w-lg pointer-events-auto"
              style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
            >
              <div
                className="mx-2 mb-2 md:mx-0 rounded-3xl border border-white/15 overflow-hidden"
                style={{
                  background: 'rgba(10, 18, 40, 0.95)',
                  backdropFilter: 'blur(24px)',
                }}
              >
                <div className="px-4 pt-4 pb-3">
                  <div className="w-10 h-1 rounded-full bg-white/20 mx-auto mb-4" />
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-black text-white">快速計時器</h3>
                    <button
                      onClick={onClose}
                      className="text-white/40 hover:text-white bg-white/5 p-2 rounded-xl transition-colors"
                    >
                      <Fi.FiX />
                    </button>
                  </div>

                  {/* Active timer */}
                  {active && (
                    <div className="mb-4 p-4 rounded-2xl border border-white/10 bg-white/[0.04]">
                      <div className="flex items-center gap-4">
                        {/* Ring */}
                        <div className="relative w-20 h-20 shrink-0">
                          <svg className="absolute inset-0 -rotate-90" viewBox="0 0 80 80">
                            <circle
                              cx="40"
                              cy="40"
                              r="32"
                              fill="none"
                              stroke="rgba(255, 255, 255, 0.08)"
                              strokeWidth="6"
                            />
                            <circle
                              cx="40"
                              cy="40"
                              r="32"
                              fill="none"
                              stroke={active.remaining === 0 ? '#fb7185' : '#34d399'}
                              strokeWidth="6"
                              strokeLinecap="round"
                              strokeDasharray={`${2 * Math.PI * 32}`}
                              strokeDashoffset={`${2 * Math.PI * 32 * (1 - progress)}`}
                              style={{ transition: 'stroke-dashoffset 0.8s linear' }}
                            />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span
                              className={`text-base font-black tabular-nums ${
                                active.remaining === 0 ? 'text-rose-400 animate-pulse' : 'text-white'
                              }`}
                            >
                              {fmt(active.remaining)}
                            </span>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-2xl">{active.emoji}</span>
                            <span className="text-sm font-black text-white truncate">{active.label}</span>
                          </div>
                          {active.remaining === 0 ? (
                            <p className="text-rose-400 font-black text-sm animate-pulse">⏰ 時間到！</p>
                          ) : (
                            <p className="text-white/40 text-xs font-bold">計時中…</p>
                          )}
                        </div>
                        <button
                          onClick={stop}
                          className="px-4 py-2.5 rounded-xl bg-rose-500/20 text-rose-300 border border-rose-500/30 font-black text-sm hover:bg-rose-500/30 transition-colors shrink-0"
                        >
                          停止
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Presets 2x2 */}
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {PRESETS.map(p => (
                      <button
                        key={p.id}
                        onClick={() => start(p.label, p.emoji, p.seconds)}
                        className="flex items-center gap-3 p-3 rounded-2xl bg-white/[0.05] border border-white/10 hover:bg-white/[0.09] transition-all active:scale-95"
                      >
                        <span className="text-xl">{p.emoji}</span>
                        <div className="text-left">
                          <p className="text-sm font-black text-white">{p.label}</p>
                          <p className="text-xs text-white/40 font-bold">{fmt(p.seconds)}</p>
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Custom */}
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="1"
                      max="999"
                      placeholder="自訂秒數"
                      value={custom}
                      onChange={e => setCustom(e.target.value)}
                      className="flex-1 bg-white/[0.07] border border-white/15 rounded-xl px-3 py-2.5 text-sm font-bold text-white placeholder-white/30 outline-none focus:border-white/40 transition-colors"
                    />
                    <button
                      onClick={() => {
                        const s = parseInt(custom);
                        if (s > 0) start('自訂計時', '⏱️', s);
                      }}
                      disabled={!custom || parseInt(custom) <= 0}
                      className="px-4 py-2.5 rounded-xl bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 font-black text-sm hover:bg-indigo-500/30 disabled:opacity-30 transition-colors"
                    >
                      開始
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <TimerCompleteModal
        open={showComplete}
        onClose={handleCompleteClose}
        label={active?.label}
        emoji={active?.emoji}
      />
    </>
  );
};

export default TimerSheet;